import { createSlice } from '@reduxjs/toolkit';
import api from 'api';
import formErrorsHandle from '../../../utils/formErrorsHandle';
import toast from 'react-hot-toast';
import { fetchNewMessagesCountAction } from '../notificationsReducer';

const initialState = {
  loading: false,
  tripsList: [],
  tripsCount: 0,
  quotesList: [],
  quotesCount: 0,
  standingOrdersList: [],
  tripComments: [],
  tripCommentsCount: 0,
  tripAttachments: [],
  tripProviders: { primary: [], secondary: [] },
  tripSummaryList: [],
  programmaticLabelTypeFilter: null
};
const tripsReducer = createSlice({
  name: 'trips',
  initialState,
  reducers: {
    setLoading(state, action) {
      return {
        ...state,
        loading: action.payload
      };
    },
    setProgrammaticLabelTypeFilter(state, action) {
      return {
        ...state,
        programmaticLabelTypeFilter: action.payload
      };
    },
    clearProgrammaticLabelTypeFilter(state) {
      return {
        ...state,
        programmaticLabelTypeFilter: null
      };
    },
    getTripsList(state, action) {
      return {
        ...state,
        tripsList: action.payload?.result,
        tripsCount: action.payload?.count
      };
    },
    getQuotesList(state, action) {
      return {
        ...state,
        quotesList: action.payload?.result,
        quotesCount: action.payload?.count
      };
    },
    getStandingOrdersList(state, action) {
      return {
        ...state,
        standingOrdersList: action.payload?.data?.childs?.result,
        standingOrdersCount: action.payload?.data?.childs?.count,
        standingOrdersInfo: action.payload?.data
      };
    },
    replaceTrip(state, action) {
      return {
        ...state,
        tripsList: state.tripsList.map((trip) =>
          trip.tripId === action.payload.tripId ? action.payload : trip
        ),
        tripsCount: action.payload?.tripsCount ?? state.tripsCount
      };
    },
    addTrip(state, action) {
      const trips = [...state.tripsList];
      trips.pop();
      return {
        ...state,
        tripsList: [action.payload, ...trips],
        tripsCount: action.payload?.tripsCount ?? state.tripsCount
      };
    },
    getTripById(state, action) {
      return {
        ...state,
        tripByIdInfo: action.payload
      };
    },
    getTripProviders(state, action) {
      return {
        ...state,
        tripProviders: action.payload
      };
    },
    getTripActions(state, action) {
      return {
        ...state,
        tripActions: action.payload?.data
      };
    },
    getHorizontalMenu(state, action) {
      return {
        ...state,
        horizontalMenu: action.payload?.data
      };
    },
    getTripComments(state, action) {
      return {
        ...state,
        tripComments: action.payload?.data?.result,
        tripCommentsCount: action.payload?.data?.count
      };
    },
    getTripAttachments(state, action) {
      return {
        ...state,
        tripAttachments: action.payload?.data
      };
    },
    invoicePdfData(state, action) {
      return {
        ...state,
        invoicePdfData: action.payload
      };
    },
    calculateTripSummary(state, action) {
      return {
        ...state,
        tripSummaryList: action.payload?.data
      };
    },
    clearTripDetail(state) {
      return {
        ...state,
        tripByIdInfo: undefined,
        tripActions: undefined,
        tripComments: [],
        tripCommentsCount: 0,
        tripAttachments: [],
        invoicePdfData: undefined,
        tripProviders: { primary: [], secondary: [] }
      };
    }
  }
});

export default tripsReducer.reducer;

export const tripsActions = tripsReducer.actions;

export const createTrip = (data, navigate, isQuote) => async (dispatch) => {
  dispatch(tripsActions.setLoading(true));
  try {
    const res = await api.post(`api/Trip`, data);
    if (res.status === 200 && res?.data) {
      navigate(isQuote ? '/dashboard/quotes' : '/dashboard/trips');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};

export const getTripsList = (data, disableLoader) => async (dispatch) => {
  if (!disableLoader) {
    dispatch(tripsActions.setLoading(true));
  }
  try {
    const res = await api.post(`api/Trip/filter`, data);
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.getTripsList(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};

export const getQuotesList = (data, disableLoader) => async (dispatch) => {
  if (!disableLoader) {
    dispatch(tripsActions.setLoading(true));
  }
  try {
    const res = await api.post(`api/Trip/filter`, data);
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.getQuotesList(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};

export const getStandingOrdersActions = (data) => async (dispatch) => {
  dispatch(tripsActions.setLoading(true));
  try {
    const res = await api.post(`api/Trip/ParentDetailed`, data);
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.getStandingOrdersList(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};
export const editTrip = (data, id, navigate, isQuote) => async (dispatch) => {
  dispatch(tripsActions.setLoading(true));
  try {
    const res = await api.put(`api/Trip`, data);
    if (res.status === 200 && res?.data) {
      navigate(isQuote ? '/dashboard/quotes' : '/dashboard/trips');
      toast.success(`Trip has been changed.`);
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};
export const getTripById = (id) => async (dispatch) => {
  try {
    const res = await api.get(`api/Trip/${id}`);
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.getTripById(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};
export const getHorizontalMenu = (id) => async (dispatch) => {
  try {
    const res = await api.get(`api/Trip/RequestersProviders`);
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.getHorizontalMenu(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};
export const getTripActions = (id) => async (dispatch) => {
  try {
    const res = await api.get(`api/Trip/PossibleActions`, { params: id });
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.getTripActions(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const putTripAction = (data, url) => async (dispatch) => {
  try {
    const res = await api.put(`api/${url}`, data);
    if (res.status === 200 && res?.data) {
      dispatch(getTripActions({ tripId: data.tripId })).catch((error) => console.error('getTripActions failed:', error));
      dispatch(getTripById(data.tripId)).catch((error) => console.error('getTripById failed:', error));
      toast.success('Action successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  }
};
export const updateTripPayor = (data) => async (dispatch) => {
  try {
    const res = await api.put(`api/Trip/UpdateTripPayor`, data);
    if (res.status === 200 && res?.data) {
      dispatch(getTripActions({ tripId: data.tripId })).catch((error) => console.error('getTripActions failed:', error));
      await dispatch(getTripById(data.tripId));
      toast.success('Action successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const acceptRejectChangeRequest =
  (data, url, method = 'put') =>
  async (dispatch) => {
    try {
      const res = await api[method](`api/${url}`, data);
      if (res.status === 200 && res?.data) {
        dispatch(getTripActions({ tripId: data.tripId })).catch((error) => console.error('getTripActions failed:', error));
        dispatch(getTripById(data.tripId)).catch((error) => console.error('getTripById failed:', error));
        toast.success('Action successfully!!!');
        return Promise.resolve(res?.data);
      }
    } catch (errors) {
      formErrorsHandle(errors.response?.data, []);
      return Promise.reject(errors.response?.data);
    }
  };
export const releaseTrip = (data, url) => async (dispatch) => {
  try {
    const res = await api.post(`api/${url}/${data.tripId}`, data);
    if (res.status === 200 && res?.data) {
      dispatch(getTripActions({ tripId: data.tripId })).catch((error) => console.error('getTripActions failed:', error));
      dispatch(getTripById(data.tripId)).catch((error) => console.error('getTripById failed:', error));
      toast.success('Action successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const getProvidersForTrip = (tripId) => async (dispatch) => {
  try {
    const primary = await api.post(`api/Provider`, {
      tripId,
      providerType: 'Primary'
    });
    const secondary = await api.post(`api/Provider`, {
      tripId,
      providerType: 'Backup'
    });
    if (primary.status === 200 && secondary.status === 200) {
      dispatch(
        tripsActions.getTripProviders({
          primary: primary?.data?.data,
          secondary: secondary?.data?.data
        })
      );
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};
export const clearTripById = () => async (dispatch) => {
  dispatch(tripsActions.getTripById(''));
};

export const clearTripDetail = () => async (dispatch) => {
  dispatch(tripsActions.clearTripDetail());
};

export const addCommentInTrip = (data) => async (dispatch) => {
  try {
    const res = await api.post(`api/Comment/Send`, data);
    if (res.status === 200 && res?.data) {
      toast.success('Comment added successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const getCommentsByTrip =
  ({ tripId, skip = 0, take = 10 }) =>
  async (dispatch) => {
    try {
      const res = await api.post(
        `api/Comment/Filter`,
        {
          paging: {
            skip,
            take
          },
          sorting: [
            {
              memberName: 'Comments.CreatedAt',
              direction: '2'
            }
          ]
        },
        { params: { tripId } }
      );
      if (res.status === 200 && res?.data) {
        dispatch(tripsActions.getTripComments(res.data));
        return Promise.resolve(res?.data);
      }
    } catch (errors) {
      formErrorsHandle(errors.response?.data, []);
      return Promise.reject(errors.response?.data);
    }
  };

export const acknowledgeMessage = (tripId) => async (dispatch) => {
  try {
    const res = await api.put(`api/Comment/Acknowledge`, null, {
      params: { tripId }
    });
    if (res.status === 200 && res?.data) {
      // Refresh the trip details to get updated labels
      // dispatch(getTripById(tripId));
      // Refresh the trips list to get updated labels
      // dispatch(tripsActions.getTripsList(res.data));
      // Refresh new messages count
      dispatch(fetchNewMessagesCountAction()).catch((error) => formErrorsHandle(error, {}));
      toast.success('Message acknowledged successfully!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const addAttachmentTrip = (data, url) => async (dispatch) => {
  try {
    const res = await api.post(`api/Trip/UploadAttachment`, data);
    if (res.status === 200 && res?.data) {
      toast.success('Upload successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const getAttachmentsListInTrip = (tripId) => async (dispatch) => {
  try {
    const res = await api.get(`api/Trip/Attachments`, { params: tripId });
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.getTripAttachments(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};
export const removeAttachmentInTrip = (id, tripId) => async (dispatch) => {
  try {
    const res = await api.delete(`api/Trip/DeleteAttachment`, {
      params: { id }
    });
    if (res.status === 200 && res?.data) {
      dispatch(getAttachmentsListInTrip({ tripId })).catch((error) => console.error('getAttachmentsListInTrip failed:', error));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const calculateTripSummary = (data) => async (dispatch) => {
  try {
    const res = await api.post(`api/PayorLevelOfService/BillingSection`, data);
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.calculateTripSummary(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};
export const checkIsDefaultChargeTripSummary = (data) => async () => {
  try {
    const res = await api.post(
      `api/PayorLevelOfService/CheckIsDefaultCharge`,
      data
    );
    if (res.status === 200 && res?.data) {
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    if (errors.response?.status !== 404) {
      formErrorsHandle(errors.response?.data, []);
    }
    return Promise.reject(errors.response?.data);
  }
};

export const getInvoicePdfData = (id) => async (dispatch) => {
  try {
    const res = await api.get(`api/Trip/GenerateInvoice/${id}`);
    if (res.status === 200 && res?.data) {
      dispatch(tripsActions.invoicePdfData(res.data));
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const payTrip = (tripId) => async (dispatch) => {
  dispatch(tripsActions.setLoading(true));
  try {
    const res = await api.put(`api/Trip/PayTrip`, {}, { params: { tripId } });
    if (res.status === 200 && res?.data) {
      toast.success(`Payment successfully!!!`);
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};

export const refundTrip = (data) => async (dispatch) => {
  dispatch(tripsActions.setLoading(true));
  try {
    const res = await api.put(`api/Trip/RefundTripPayment`, data);
    if (res.status === 200 && res?.data) {
      toast.success(`Refund successfully!!!`);
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};
export const sendPaymentAction = (data) => async (dispatch) => {
  dispatch(tripsActions.setLoading(true));
  try {
    const res = await api.post(`api/TripBillingAction`, data);
    if (res.status === 200 && res?.data) {
      toast.success('Payment action submitted successfully!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};

export const processPayment = (tripId, cardData) => async (dispatch) => {
  dispatch(tripsActions.setLoading(true));
  try {
    const res = await api.post(`api/Trip/ProcessPayment/${tripId}`, cardData);
    if (res.status === 200 && res?.data) {
      toast.success('Payment processed successfully!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    return Promise.reject(errors.response?.data);
  } finally {
    dispatch(tripsActions.setLoading(false));
  }
};

export const takeTripAction = (tripId) => async (dispatch) => {
  try {
    const res = await api.post(`api/Trip/SelfAccept`, { tripId });
    if (res.status === 200 && res?.data) {
      // await dispatch(getTripActions({ tripId: tripId }));
      // await dispatch(getTripById(tripId));
      toast.success('Action successfully!!!');
      return Promise.resolve(res?.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};

export const exportTripsCsv = (filterParams) => async (dispatch) => {
  try {
    const res = await api.post('api/Trip/ExportCsv', filterParams, {
      responseType: 'blob', // Important for file download
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (res.status === 200 && res.data) {
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      link.setAttribute('download', `trips_export_${dateStr}.csv`);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.remove();
      window.URL.revokeObjectURL(url);

      // Show success message
      toast.success('CSV file exported successfully!');

      return Promise.resolve(res.data);
    }
  } catch (errors) {
    formErrorsHandle(errors.response?.data, []);
    return Promise.reject(errors.response?.data);
  }
};
